import { useCanManageProfitability } from "@/profitability/access";
import { type DocumentLinks, DocumentsTab } from "@/profitability/documents";
import type { DocumentsRun } from "@/profitability/documents/documents-tab";
import { objectsOf } from "@/profitability/records/objects";
import { useReports } from "@/profitability/report-query";
import {
  profitabilityNewRunPath,
  profitabilityPeriodPath,
} from "@/profitability/routes";
import type { ProfitabilityRun, ReportOutput } from "@/profitability/types";

/**
 * The Documents tab of a run's own page (a draft's sample, a run that has
 * not finished): the same tab the template page has, with the template's
 * inputs looked up, no record tabs to link to and no chat to ask.
 */
export const RunDocumentsTab = ({
  businessId,
  run,
  output,
}: {
  businessId: string;
  run: DocumentsRun & Pick<ProfitabilityRun, "report">;
  output?: ReportOutput;
}) => {
  const reports = useReports(run.report ? businessId : null);
  const report = reports.data?.find((item) => item.id === run.report?.id);
  const canUpload =
    useCanManageProfitability() && !!report?.published && !report.archived_at;
  const links: DocumentLinks = {
    upload: (slotKey) =>
      canUpload && report
        ? profitabilityNewRunPath(
            report.id,
            run.start_date && run.end_date
              ? {
                  start_date: run.start_date,
                  end_date: run.end_date,
                  input: slotKey,
                  from: run.id,
                }
              : null,
          )
        : null,
    record: () => null,
    period: (runId, reportId, documentId) =>
      reportId
        ? `${profitabilityPeriodPath(reportId, runId, "documents")}&doc=${encodeURIComponent(documentId)}`
        : null,
  };
  return (
    <DocumentsTab
      businessId={businessId}
      run={run}
      slots={report?.inputs ?? []}
      output={output}
      objects={objectsOf(output)}
      links={links}
      canUpload={canUpload}
    />
  );
};
